/**
 * OpportunityCard Component - E007-S007 (PRD-3682)
 * Displays a single AI-detected optimization opportunity
 */

import React from 'react';
import type { Opportunity } from '../types/opportunities';
import {
  opportunityTypeIcons,
  effortIcons,
  effortColors,
  impactTypeLabels,
} from '../types/opportunities';
import { formatCurrency } from '../lib/formatters';

interface OpportunityCardProps {
  opportunity: Opportunity;
  onViewDetails: (id: string) => void;
  onDismiss: (id: string) => void;
}

export default function OpportunityCard({
  opportunity,
  onViewDetails,
  onDismiss,
}: OpportunityCardProps) {
  const { id, type, title, description, impact, effort, confidence, affected_items } = opportunity;

  // Format impact amount
  const impactText = `${impactTypeLabels[impact.type]} ${formatCurrency(impact.amount)}/${impact.period.replace('per_', '')}`;

  // Confidence as percentage
  const confidencePercent = Math.round(confidence * 100);

  return (
    <div className="relative bg-white border border-gray-200 rounded-lg p-5 hover:shadow-md transition-shadow duration-200">
      {/* Dismiss button */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onDismiss(id);
        }}
        className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
        aria-label="Dismiss opportunity"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>

      {/* Header with icon and type */}
      <div className="flex items-start gap-3 mb-3">
        <span className="text-2xl" role="img" aria-label={type}>
          {opportunityTypeIcons[type]}
        </span>
        <div className="flex-1">
          <h3 className="text-base font-semibold text-gray-900 pr-8">
            {title}
          </h3>
        </div>
      </div>

      {/* Description */}
      <p className="text-sm text-gray-600 mb-4 line-clamp-2">
        {description}
      </p>

      {/* Impact & Metrics Row */}
      <div className="flex items-center gap-4 mb-4 text-sm">
        {/* Impact */}
        <div className="flex items-center gap-1.5">
          <span className="text-green-600 font-medium">
            {impactText}
          </span>
        </div>

        {/* Effort */}
        <div className={`flex items-center gap-1 px-2 py-1 rounded ${effortColors[effort]}`}>
          <span>{effortIcons[effort]}</span>
          <span className="font-medium capitalize">{effort} effort</span>
        </div>

        {/* Confidence */}
        <div className="flex items-center gap-1 text-gray-600">
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
          <span className="font-medium">{confidencePercent}%</span>
        </div>
      </div>

      {/* Affected Items */}
      <div className="flex items-center gap-2 mb-4 text-sm text-gray-500">
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
        </svg>
        <span>
          Affects {affected_items.length} {affected_items.length === 1 ? affected_items[0].type : 'items'}
        </span>
      </div>

      {/* Action Button */}
      <button
        onClick={() => onViewDetails(id)}
        className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 px-4 rounded-lg transition-colors duration-200"
      >
        View Details
      </button>
    </div>
  );
}
